import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { studioMeta } from '@/constants/site'

interface WordmarkProps {
    className?: string
    size?: string
    tone?: 'dark' | 'light'
    animate?: boolean
}

/* The full studio lockup: the wordmark set huge, the registered mark hung off
   its top-right corner, and the script descriptor tucked under the last
   letter in the accent colour. */
export const Wordmark: FC<WordmarkProps> = ({
    className,
    size = 'clamp(88px,17vw,231px)',
    tone = 'dark',
    animate = true
}): ReactNode => {
    const scope = useRef<HTMLDivElement>(null)

    useGsap(() => {
        const root = scope.current

        if (!root || !animate || prefersReducedMotion()) {
            return
        }

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: root,
                start: 'top 88%',
                once: true
            }
        })

        tl.fromTo(root.querySelectorAll('[data-wordmark-letter]'),
            { yPercent: 150 },
            { yPercent: 0, duration: 1.15, stagger: 0.055, ease: 'noma' },
            0
        )

        tl.fromTo(root.querySelector('[data-wordmark-mark]'),
            { opacity: 0, scale: 0.4, rotate: -30 },
            { opacity: 1, scale: 1, rotate: 0, duration: 0.9, ease: 'noma' },
            0.35
        )

        tl.fromTo(root.querySelector('[data-wordmark-script]'),
            { opacity: 0, x: -34, rotate: -7 },
            { opacity: 1, x: 0, rotate: 0, duration: 1.1, ease: 'noma' },
            0.45
        )
    }, scope, [animate])

    return (
        <div ref={scope} className={cn('relative w-fit', className)}>
            <span
                className={cn(
                    't-display flex overflow-hidden py-[0.22em] -my-[0.22em] leading-[0.8]',
                    tone === 'dark' ? 'text-ink' : 'text-paper'
                )}
                style={{ fontSize: size }}
            >
                {Array.from('noma').map((letter, index) => (
                    <span
                        key={`${letter}-${index}`}
                        data-wordmark-letter
                        className='inline-block'
                        style={animate ? { transform: 'translateY(150%)' } : undefined}
                    >
                        {letter}
                    </span>
                ))}
            </span>

            <span
                data-wordmark-mark
                className={cn(
                    't-display absolute left-[98%] top-[12%] leading-[0.7]',
                    tone === 'dark' ? 'text-ink' : 'text-paper'
                )}
                style={{ fontSize: `calc(${size} * 0.177)` }}
                aria-hidden='true'
            >
                {studioMeta.mark}
            </span>

            <span
                data-wordmark-script
                className='t-script absolute bottom-0 right-0 translate-y-[26%] leading-[0.62] text-brand'
                style={{ fontSize: `calc(${size} * 0.336)` }}
                aria-hidden='true'
            >
                Studio
            </span>
        </div>
    )
}
